import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { createElement, type ReactNode } from 'react';

import {
  defineFieldReadModule,
  type FieldReadResource,
  type FieldReadResourceState
} from '../field-read-module.ts';
import {
  EMPTY_ENTRY_LINK_CONTENT,
  type EntryLinkReadContent,
  type EntryLinkItemContent
} from './entry-link-read-content.ts';
import { EntryLinkValueView } from './entry-link-value-view.tsx';

export type EntryLinkDatabaseResourceInput = {
  readonly contentId: string | undefined;
  readonly databaseId: string | undefined;
  readonly databaseSpaceKey: string | undefined;
};

export type EntryLinkResolvedEntry = {
  readonly entryId: string;
  readonly title: string | undefined;
  readonly href: string;
};

export type EntryLinkDatabaseResourceValue = {
  readonly hidden?: boolean;
  readonly getEntry: (entryId: string) => EntryLinkResolvedEntry | undefined;
};

export type EntryLinkReadPolicy = {
  readonly brokenLabel: string;
  readonly unnamedLabel: string;
  readonly getDatabaseSnapshot?: (
    input: EntryLinkDatabaseResourceInput
  ) => EntryLinkDatabaseResourceValue | undefined;
  readonly getDatabaseResource?: (
    input: EntryLinkDatabaseResourceInput
  ) => FieldReadResource<EntryLinkDatabaseResourceValue>;
};

type CompiledEntryLink = {
  readonly key: string;
  readonly entryId: string;
};

function compileLinks(value: DatabaseValue | undefined): readonly CompiledEntryLink[] {
  if (value?.type !== DatabaseFieldType.ENTRY_LINK) {
    return [];
  }

  return value.value.flatMap((item) =>
    'entryId' in item && typeof item.entryId === 'string'
      ? [
          Object.freeze({
            key: item._id,
            entryId: item.entryId
          })
        ]
      : []
  );
}

function createContent(
  links: readonly CompiledEntryLink[],
  policy: Partial<EntryLinkReadPolicy>,
  state: FieldReadResourceState<EntryLinkDatabaseResourceValue>
): EntryLinkReadContent {
  if (links.length === 0 || (state.status === 'ready' && state.value.hidden)) {
    return EMPTY_ENTRY_LINK_CONTENT;
  }

  const contentLinks = links
    .filter((link) => link.entryId)
    .map((link): EntryLinkItemContent => {
      const resolved =
        state.status === 'ready' ? state.value.getEntry(link.entryId) : undefined;
      const label = resolved ? resolved.title || policy.unnamedLabel : policy.brokenLabel;

      return Object.freeze({
        key: link.key,
        entryId: link.entryId,
        label: label ?? link.entryId,
        href: resolved?.href,
        status: resolved ? 'ready' : 'broken'
      });
    });

  return Object.freeze({ kind: 'entry-link', links: Object.freeze(contentLinks) });
}

/**
 * Compiles Entry Link defaults and one bounded related-database read.
 */
export type EntryLinkReadRenderer = (content: EntryLinkReadContent) => ReactNode;

export function createEntryLinkFieldReadModule(
  options?: EntryLinkReadPolicy & { readonly render?: EntryLinkReadRenderer }
) {
  const render = options?.render;
  const policy: Partial<EntryLinkReadPolicy> = options ?? {};

  return defineFieldReadModule<EntryLinkReadContent>({
    fieldType: DatabaseFieldType.ENTRY_LINK,
    render: render ?? ((content) => createElement(EntryLinkValueView, { content })),
    compile(field: DatabaseField) {
      const contentId = field.settings.contentId;
      const databaseId = field.settings.databaseId;
      const databaseSpaceKey = field.settings.databaseSpace?.key;
      const input = { contentId, databaseId, databaseSpaceKey };
      const loadingWidth = field.width ? Math.min(field.width - 60, 100) : 100;
      const hasDatabaseIdentity = Boolean(contentId || (databaseId && databaseSpaceKey));

      function getLinks(
        storedValue: DatabaseValue | undefined,
        defaultValue: DatabaseValue | undefined
      ): readonly CompiledEntryLink[] {
        return compileLinks(storedValue ?? defaultValue);
      }

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ) {
          if (!hasDatabaseIdentity) {
            return EMPTY_ENTRY_LINK_CONTENT;
          }

          const links = getLinks(storedValue, defaultValue);
          const cached = policy.getDatabaseSnapshot?.(input);

          return cached
            ? createContent(links, policy, { status: 'ready', value: cached })
            : contentId && links.length
              ? Object.freeze({
                  kind: 'entry-link' as const,
                  loadingWidth,
                  links: Object.freeze([])
                })
              : createContent(links, policy, { status: 'unavailable' });
        },
        getResourceRead({ storedValue, defaultValue }) {
          if (
            !policy.getDatabaseResource ||
            !hasDatabaseIdentity ||
            policy.getDatabaseSnapshot?.(input)
          ) {
            return undefined;
          }

          const links = getLinks(storedValue, defaultValue);

          if (links.length === 0) {
            return undefined;
          }

          const resource = policy.getDatabaseResource({
            contentId,
            databaseId,
            databaseSpaceKey
          });
          const fallbackContent: EntryLinkReadContent = Object.freeze({
            kind: 'entry-link',
            loadingWidth,
            links: Object.freeze([])
          });

          return Object.freeze({
            resources: Object.freeze([resource]),
            resolve(
              read: <Value>(
                requested: FieldReadResource<Value>
              ) => FieldReadResourceState<Value>
            ) {
              const state = read(resource);

              return state.status === 'loading'
                ? fallbackContent
                : createContent(links, policy, state);
            }
          });
        }
      });
    }
  });
}
